import { useEffect, useRef, useState } from 'react';

/**
 * Ticks a countdown once a second between server refetches.
 *
 * The server's `remainingSeconds` is the only authority (FR-U-34); this just
 * interpolates. On tab focus it recomputes from the absolute expiry so a
 * backgrounded tab — where browsers throttle timers hard — never shows a frozen
 * or drifted clock.
 */
export function useCountdown(expiresAt: string, serverRemainingSeconds: number): number {
  const anchor = useRef({ at: Date.now(), remaining: serverRemainingSeconds });
  const [remaining, setRemaining] = useState(serverRemainingSeconds);

  useEffect(() => {
    const at = Date.now();
    const untilExpiry = Math.max(0, Math.floor((new Date(expiresAt).getTime() - at) / 1000));
    const syncedRemaining = Math.min(serverRemainingSeconds, untilExpiry);
    anchor.current = { at, remaining: syncedRemaining };
    setRemaining(syncedRemaining);
  }, [serverRemainingSeconds, expiresAt]);

  useEffect(() => {
    const tick = () => {
      const elapsed = Math.floor((Date.now() - anchor.current.at) / 1000);
      setRemaining(Math.max(0, anchor.current.remaining - elapsed));
    };
    const timer = window.setInterval(tick, 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        // Trust the absolute expiry after a throttled background stretch.
        const absolute = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
        anchor.current = { at: Date.now(), remaining: Math.max(0, absolute) };
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [expiresAt]);

  return remaining;
}

/** Live online/offline state — drives the offline banner. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}
